import React, { useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Container, Row, Col } from "react-bootstrap"
import ReactPlayer from "react-player"
import { css } from "@emotion/core"
import PropTypes from "prop-types"
import Logo from "../assets/images/header-logo.png"

import Layout from "../components/Layout"
import VideoDescription from "../components/VideoDescription"
import PatientBio from "../components/PatientBio"
import LearnAbout from "../components/about/LearnAbout"
import QuestionsToAsk from "../components/QuestionsToAsk"
import WatchNextVideo from "../components/WatchNextVideo"

const VideoAssetTemplate = ({ data }) => {
  const [isVideoClicked, setIsVideoClicked] = useState(false)
  const WATCH_NEXT_MAP = {
    'Newly Diagnosed': [
      'Hodgkin Lymphoma – An Attentive Doctor is Key',
      'Hodgkin Lymphoma - The Importance of the Doctor-Patient Relationship',
      'Hodgkin Lymphoma - Becoming Your Best Advocate',
      'Hodgkin Lymphoma Treatment Questions',
      'Self-Advocacy During a Hodgkin Lymphoma Diagnosis '
    ],
    'Making Treatment Decisions': [
      'Hodgkin Lymphoma - Getting a Second Opinion',
      'How to Approach an Advanced Hodgkin Lymphoma Treatment Decision',
      'Evaluating All Potential Hodgkin Lymphoma Treatment Options',
      'Discussing Hodgkin Lymphoma Treatment Options with Your Oncologist',
      'Dealing with Emotions Before Hodgkin Lymphoma Treatment'
    ],
    'Preparing for Treatment': [
      'Preparing for Treatment Conversations with Your Oncologist ',
      'Dealing with Hodgkin Lymphoma as a College Student',
      'Discussing Treatment Options for Hodgkin Lymphoma',
      'Life Outlook While Going Through Hodgkin Lymphoma',
      'Hodgkin Lymphoma - Maintaining a Positive Mindset'
    ],
    'Managing Treatment': [
      'Adjusting to Life After Hodgkin Lymphoma Diagnosis',
      'Hodgkin Lymphoma - Value of a Strong Support System ',
      'Becoming a Resource for Other Hodgkin Lymphoma Patients',
      'Challenges During Hodgkin Lymphoma Treatment',
      'Hodgkin Lymphoma - Positivity After Hodgkin Lymphoma Diagnosis'
    ]
  };

  const {
    title,
    tags,
    ordering,
    videoPagePosterImage,
    videoUrl,
    description,
    /* patientBio is an array
    Patient Bio reference set to many instead of one
    May be a good thing if in the future multiple bios
    can be on a page. For now, the code takes the first
    bio.
    */
    thumbnail,
    patientBio,
    questionsToAskButton,
    shouldLearnAboutCalloutAppear,
    meta,
  } = data.contentfulVideoAsset

  const WATCH_NEXT_LIST = WATCH_NEXT_MAP[tags[0]];
  const duration = meta.duration ? meta.duration : "PT1M30S";
  let videoSrc = videoUrl;
  let noCookie = false;
  if (videoUrl.includes('youtube-nocookie')) {
    noCookie = true;
    videoSrc = videoSrc.replace("youtube-nocookie", "youtube");
  }

  let whatsNextCounter = 1;

  return (
    <Layout
      title={meta.title}
      description={meta.description}
      canonicalURL={meta.canonicalURL}
      OGTitle={meta.OGTitle}
      OGDescription={meta.OGDescription}
      OGImage={meta.OGImageURL || Logo}
      OGUrl={meta.OGUrl}
      OGType='Website'
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{"@context": "https://schema.org","@type": "VideoObject", "description": "${meta.description}","duration": "${duration}","name": "${meta.OGTitle}","thumbnail": "${thumbnail.fluid.src}"}` }} />
      <Container
        css={css`
          h1 {
            font-size: 28px;
            font-weight: bold;
            line-height: 32px;
            color: #69696a;
            text-transform: uppercase;
          }
          h2 {
            font-size: 28px;
            font-weight: bold;
            line-height: 32px;
            color: #69696a;
            text-transform: uppercase;
          }
        `}
      >
        <Row>
          <Col lg={9} xl={8}>
            <Row>
              <Col
                css={css`
                  margin: 16px 0;
                  font-size: 14px;
                  line-height: 34px;
                `}
              >
                <a
                  css={css`
                    color: #01aef1;
                    &:hover {
                      text-decoration: none;
                      color: #7f3f95;
                    }
                  `}
                  href="/videos/"
                >
                  &lt; BACK TO VIDEO LIBRARY
                </a>
                {/* {" "}
                |{" "}
                <span
                  css={css`
                    text-transform: uppercase;
                  `}
                >
                  {tags[0]}
                </span> */}
              </Col>
            </Row>
            <Row>
              <Col onClick={() => setIsVideoClicked(true)}>
                <div
                  css={css`
                    position: relative;
                    padding-top: 56.25%;
                    margin-bottom: 24px;
                  `}
                >
                  <ReactPlayer
                    width="100%"
                    height="100%"
                    url={videoSrc}
                    controls={true}
                    playing
                    config={{
                      youtube: {
                        embedOptions: {
                          host: noCookie && 'https://www.youtube-nocookie.com'
                        }
                      }
                    }}
                    css={css`
                      position: absolute;
                      top: 0;
                      left: 0;
                      .react-player__shadow {
                        display: none !important;
                      }
                    `}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col>
                <h1>{title}</h1>
                <div
                  css={css`
                    font-size: 18px;
                    line-height: 21px;
                    margin-bottom: 32px;
                    a {
                      color: #01aef1;
                      font-weight: bold;
                      &:hover {
                        text-decoration: none;
                        color: #7f3f95;
                      }
                    }
                  `}
                >
                  <VideoDescription json={description.json} />
                </div>
              </Col>
            </Row>
            {patientBio && (
              <>
                <Row>
                  <Col>
                    <PatientBio bio={patientBio[0]} />
                  </Col>
                </Row>
              </>
            )}
            {questionsToAskButton ? <QuestionsToAsk /> : ""}
            {shouldLearnAboutCalloutAppear && <LearnAbout />}
          </Col>

          <Col
            lg={3}
            xl={4}
            css={css`
              margin-top: 65px;
              padding-bottom: 10px;
              height: fit-content;
              background: #eeeeee;
            `}
          >
            <div
              css={css`
                padding-top: 30px;
              `}
            >
              <h2 css={css`
                margin-bottom: 20px;
              `}>
                Watch Next
              </h2>
              {/* {tags} */}
              {data.allContentfulVideoAsset.edges
                .sort(() => Math.random() - Math.random()).slice(0, 5)
                .map(({ node }, i) => {
                  if (title != node.title) {
                    return (
                      <Row>
                        <Col
                          sm={1}
                          css={css`
                            align-self: center;
                          `}
                        >
                          <p
                            css={css`
                              color: #01aef1;
                              font-size: 24px;
                              margin-bottom: 10px;
                            `}
                          >
                            {
                              //i + 1
                              whatsNextCounter++
                            }
                            {/* {title === node.title ? (
                              // <img
                              //   loading='lazy'
                              //   css={css`
                              //     color: #01aef1;
                              //     text-shadow: 3px 5px 9px rgba(0, 0, 0, 0.82);
                              //     font-size: 40px;
                              //     ${[mq[2]]} {
                              //       font-size: 30px;
                              //     }
                              //     path {
                              //       color: inherit;
                              //       text-shadow: inherit;
                              //     }
                              //   `}
                              //   src={arrowRight}
                              // />
                              i + 1
                            ) : (
                              i + 1
                            )} */}
                          </p>
                        </Col>
                        <Col
                          sm={5}
                          lg={10}
                          xl={5}
                          css={css`
                            align-self: center;
                            margin-bottom: 10px;
                          `}
                        >
                          {node.title}
                        </Col>
                        <Col
                          sm={6}
                          lg={12}
                          xl={6}
                          css={css`
                            margin-bottom: 24px;
                          `}
                        >
                          <WatchNextVideo
                            key={`home ${node.tags[0]}`}
                            img={node.thumbnail.fluid.src}
                            category={node.tags[0]}
                            slug={node.urlSlug}
                            nowViewing={title === node.title}
                            alt={meta.alt}
                          />
                        </Col>
                      </Row>
                    )
                  }
                })
              }
            </div>
          </Col>
        </Row>
      </Container>
    </Layout>
  )
}

VideoAssetTemplate.propTypes = {
  data: PropTypes.object.isRequired,
}

export default VideoAssetTemplate

export const query = graphql`
  query($slug: String!) {
    contentfulVideoAsset(urlSlug: { eq: $slug }) {
      tags
      videoUrl
      videoPagePosterImage {
        fluid {
          ...GatsbyContentfulFluid
        }
      }
      thumbnail {
        fluid {
          src
        }
      }
      title
      description {
        json
        childContentfulRichText {
          html
        }
      }
      meta {
        title
        description
        duration
        canonicalURL
        OGTitle
        OGDescription
        OGUrl
        alt
      }
      patientBio {
        name
        ageDiagnosed
        photo {
          fixed {
            ...GatsbyContentfulFixed
          }
        }
        biography {
          biography
        }
      }
      questionsToAskButton
      shouldLearnAboutCalloutAppear
    }
    allContentfulVideoAsset {
      edges {
        node {
          id
          ordering
          urlSlug
          videoUrl
          tags
          title
          thumbnail {
            fluid {
              src
            }
          }
        }
      }
    }
  }
`
